import React, { useState } from "react";
import {
    Stepper,
    Step,
    StepLabel,
    Button,
    TextField,
    Paper,
    makeStyles,
    Grid,
} from "@material-ui/core";
import CreateCargoContractForm from "../Orders/createCargoContractModel";
import VesselContractForm from "../../../components/Calculator/AddCalculatorFleet/createVesselContractModel";
import moment from "moment";
import { toastError, validateCargoContract, validateVesselContract } from "../../../utils";

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: theme.spacing(2),
    },
    button: {
        marginLeft: theme.spacing(1),
    },
}));

const SimpleForm = (props) => {
    const { formData = {}, handleChange = () => { } } = props;
    return (
        <Paper elevation={0} style={{ padding: 16 }} className='max-w-2xl mx-auto'>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Estimation Date"
                        variant="outlined"
                        value={moment().format('DD-MM-YYYY')}
                        disabled
                        size="small"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Contract Name"
                        required
                        variant="outlined"
                        name='contractName'
                        value={formData.contractName || ""}
                        onChange={handleChange}
                        size="small"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Comments"
                        variant="outlined"
                        name="comments"
                        value={formData.comments}
                        onChange={handleChange}
                        multiline
                        rows={2}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

export const EstimateToContractStepper = ({
    vesselFormData,
    setVesselFormData,
    cargoFormData,
    setCargoFormData,
    cargoAttachments,
    vesselAttachments,
    handleCargoAttachmentUpload,
    handleVesselAttachmentUpload,
    handleCargoDataChange,
    handleVesselDataChange,
    handleVesselTextContractUpload,
    handleCargoTextContractUpload,
    modelType,
    removeCargoAttachment,
    removeVesselAttachment,
    setCargoTextContract,
    setVesselTextContract,
    cargoTextContract,
    vesselTextContract,
    handleFinish,
    estimateFormData,
    setEstimateFormData,
}) => {
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);

    // Get steps based on modelType
    const getSteps = () => {
        switch (modelType) {
            case "cargo-cargo":
                return ["Create Cargo Contract", "Contract Estimate Information"];
            case "vessel-vessel":
                return ["Create Vessel Contract", "Contract Estimate Information"];
            case "vessel-cargo":
                return ["Create Cargo Contract", "Create Vessel Contract", "Contract Estimate Information"];
            default:
                return [];
        }
    };

    const steps = getSteps();

    const handleSimpleFormChange = (e) => {
        const { name, value } = e.target;
        setEstimateFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleNext = () => {
        const currentStepLabel = steps[activeStep];
        if (currentStepLabel === "Create Cargo Contract") {
            const errorMsg = validateCargoContract(cargoFormData);
            if (errorMsg) {
                toastError(errorMsg);
                return;
            }
        } else if (currentStepLabel === "Create Vessel Contract") {
            const errorMsg = validateVesselContract(vesselFormData);
            if (errorMsg) {
                toastError(errorMsg);
                return;
            }
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    // Render content based on modelType and step
    const renderStepContent = () => {
        if (modelType === "vessel-cargo") {
            return (
                activeStep === 0 ? (
                    <CreateCargoContractForm
                        formData={cargoFormData}
                        setFormData={setCargoFormData}
                        handleChange={handleCargoDataChange}
                        removeAttachment={removeCargoAttachment}
                        textContract={cargoTextContract}
                        handleTextContractUpload={handleCargoTextContractUpload}
                        handleAttachmentUpload={handleCargoAttachmentUpload}
                        attachments={cargoAttachments}
                        setTextContract={setCargoTextContract}
                    />
                ) : activeStep === 1 ? (
                    <VesselContractForm
                        formData={vesselFormData}
                        setFormData={setVesselFormData}
                        handleChange={handleVesselDataChange}
                        attachments={vesselAttachments}
                        textContract={vesselTextContract}
                        removeAttachment={removeVesselAttachment}
                        handleTextContractUpload={handleVesselTextContractUpload}
                        handleAttachmentUpload={handleVesselAttachmentUpload}
                        setTextContract={setVesselTextContract}
                    />
                ) : activeStep === 2 ? (
                    <SimpleForm
                        formData={estimateFormData}
                        handleChange={handleSimpleFormChange}
                    />
                ) : null
            );
        }

        if (modelType === "cargo-cargo") {
            return activeStep === 0 ? (
                <CreateCargoContractForm
                    formData={cargoFormData}
                    setFormData={setCargoFormData}
                    handleChange={handleCargoDataChange}
                    removeAttachment={removeCargoAttachment}
                    textContract={cargoTextContract}
                    handleTextContractUpload={handleCargoTextContractUpload}
                    handleAttachmentUpload={handleCargoAttachmentUpload}
                    attachments={cargoAttachments}
                    setTextContract={setCargoTextContract}
                />
            ) : (
                <SimpleForm
                    formData={estimateFormData}
                    handleChange={handleSimpleFormChange}
                />
            );
        }

        if (modelType === "vessel-vessel") {
            return (
                activeStep === 0 ? (
                    <VesselContractForm
                        formData={vesselFormData}
                        setFormData={setVesselFormData}
                        handleChange={handleVesselDataChange}
                        attachments={vesselAttachments}
                        textContract={vesselTextContract}
                        removeAttachment={removeVesselAttachment}
                        handleTextContractUpload={handleVesselTextContractUpload}
                        handleAttachmentUpload={handleVesselAttachmentUpload}
                        setTextContract={setVesselTextContract}
                    />
                ) : activeStep === 1 ? (
                    <SimpleForm
                        formData={estimateFormData}
                        handleChange={handleSimpleFormChange}
                    />
                ) : null
            );
        }

        return null;
    };

    const handleFinishClick = () => {
        if (!estimateFormData?.contractName || !estimateFormData.contractName.trim()) {
            toastError("Contract Name is Required");
            return;
        }
        handleFinish();
    };

    return (
        <div className={classes.root}>
            {steps.length > 1 && (
                <Stepper
                    activeStep={activeStep}
                    className="stepper-background"
                    style={{ background: "#fff" }}
                    alternativeLabel
                >
                    {steps.map((label) => (
                        <Step style={{ background: "#fff" }} key={label}>
                            <StepLabel style={{ background: "#fff" }}>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}

            <Paper elevation={0} className={classes.stepContent}>
                {renderStepContent()}
            </Paper>

            <div className="flex justify-end gap-2 mt-2">
                {activeStep !== 0 && steps.length > 1 && (
                    <Button
                        size="small"
                        onClick={handleBack}
                        style={{ textTransform: 'none', height: '24px', fontSize: '11px', fontWeight: '500', borderRadius: '0px', borderColor: '#cbd5e1' }}
                        variant="outlined"
                    >
                        Back
                    </Button>
                )}
                {activeStep < steps.length - 1 ? (
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleNext}
                        style={{ textTransform: 'none', height: '24px', fontSize: '11px', fontWeight: '500', borderRadius: '0px', backgroundColor: '#1e293b', color: '#fff' }}
                    >
                        Next
                    </Button>
                ) : (
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleFinishClick}
                        style={{ textTransform: 'none', height: '24px', fontSize: '11px', fontWeight: '500', borderRadius: '0px', backgroundColor: '#1e293b', color: '#fff' }}
                    >
                        Finish
                    </Button>
                )}
            </div>
        </div>
    );
};
